import { Modal } from 'almanac-ui/Modal'
import { useEffect, useMemo, useRef, useState } from 'react'
import { WizardsProps } from '../Types'
import { SearchInput } from 'components/SearchInput/SearchInput'
import Styles from './WizardsV3.module.scss'
import { useHover } from '@react-aria/interactions'
import { Search } from 'blaze-ui/icons'
import { Sidebar } from './Sidebar/Sidebar'
import {
  AllSubCategories,
  CategoryTypes,
  ChannelKeyType,
  CREATE_NEW_EXCLUDED_CHANNELS,
  ForYouPages,
  VIRTUAL_MARKETER_CREATE_NEW_EXCLUDED_CHANNELS,
} from './Types'
import { CATEGORIES_DESKTOP, GROUPS_V2 } from './constants'
import { ForYou } from './Sections/ForYou'
import { useSearchGroups } from '../Catalogue/useGroupSearch'
import { SearchSection } from './Sections/SearchSection'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useRecentWizards } from 'hooks/useRecentWizards'
import { useFilteredRecentWizards } from './hooks/useFilteredRecentWizards'
import { observer } from 'mobx-react-lite'
import { useRegistrationData } from 'hooks/queries/users/useRegistrationData'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import ChevronLeftSmall from 'blaze-ui/icons/24/ChevronLeftSmall'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import cx from 'classnames'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

export const SHOW_RECENT_WIZARDS_MAX = 4
export const SHOW_RECENT_WIZARDS_MAX_V1 = 8
export const SHOW_RECENT_WIZARDS_MIN = 4

export const WizardsV3 = observer(
  ({ initialValues, initialCategory, initialSubCategory, allowNewProject = true, onSuccess, onBack }: WizardsProps) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const [search, setSearch] = useState('')
    const searchRef = useRef<HTMLInputElement | null>(null)
    const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer
    const { hoverProps, isHovered } = useHover({})

    const [selectedCategory, setSelectedCategory] = useState<CategoryTypes>(
      (initialCategory as CategoryTypes) || 'For You',
    )
    const [prevCategory, setPrevCategory] = useState<CategoryTypes | undefined>(undefined)
    const [subCategory, setSubCategory] = useState<AllSubCategories | undefined>(
      (initialSubCategory as AllSubCategories) || undefined,
    )
    const [isVisible, setIsVisible] = useState(false)

    const { data } = useRegistrationData({ cacheTime: Infinity })
    const marketingExperience = data?.user_registration_data?.marketing_experience_level ?? 0

    const excludedChannels: readonly ChannelKeyType[] = isVirtualMarketer
      ? VIRTUAL_MARKETER_CREATE_NEW_EXCLUDED_CHANNELS
      : CREATE_NEW_EXCLUDED_CHANNELS
    const recentWizardsQuery = useRecentWizards({ count: SHOW_RECENT_WIZARDS_MAX_V1 })
    const recentWizards = useFilteredRecentWizards(recentWizardsQuery.data?.data?.channels)?.filter(
      (channel) => !excludedChannels.includes(channel),
    )

    const searchGroups = useSearchGroups()

    const searchResults = useMemo(() => {
      const filteredGroups = searchGroups(GROUPS_V2, search || '')
      const searchResults = filteredGroups.flatMap((group) => group.channels)

      return searchResults.filter((channel) => !excludedChannels.includes(channel))
    }, [excludedChannels, search, searchGroups])

    const parentFolder = initialValues?.project?.database_mode ? undefined : initialValues?.project
    const showNewProjectButton = allowNewProject && !initialValues?.project?.database_mode

    const categories = CATEGORIES_DESKTOP

    const category = categories.find((c) => c.name === selectedCategory) ?? categories[0]

    const forYouPageType: ForYouPages = useMemo(() => {
      if (recentWizards) {
        return 'recently_used'
      }

      if (marketingExperience > 1) {
        return 'power_up'
      }

      return 'getting_started'
    }, [marketingExperience, recentWizards])

    // wait until content is settled before showing the section
    useEffect(() => {
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 100)

      return () => clearTimeout(timer)
    }, [selectedCategory])

    useEffect(() => {
      searchRef.current?.focus()
    }, [selectedCategory])

    useEffect(() => {
      if (initialSubCategory) {
        setIsVisible(true)
      }
    }, [initialSubCategory])

    const handleBack = () => {
      setSubCategory(undefined)
      if (prevCategory) {
        setSelectedCategory(prevCategory)
        setPrevCategory(undefined)
      }
    }

    const _setSelectedCategory = (category: CategoryTypes) => {
      if (category !== selectedCategory) {
        setIsVisible(false)
      }
      setSelectedCategory(category)
      setSubCategory(undefined)
      setPrevCategory(undefined)
      setSearch('')
    }

    const renderSection = () => {
      const props = {
        initialValues,
        onSuccess,
        recentWizards,
        forYouPageType,
        subCategory,
        setSubCategory,
        setSelectedCategory,
        setPrevCategory,
      }

      if (search.length > 0) {
        return <SearchSection search={search} searchResults={searchResults} {...props} />
      }

      if (category) {
        return <category.component searchRef={searchRef} {...props} />
      } else {
        return <ForYou {...props} />
      }
    }

    return (
      <Modal.Root
        size="lg"
        isKeyboardDismissDisabled={search.length > 0}
        bodyClassName={Styles.modalBody}
        aria-labelledby="create-new-header"
      >
        <Modal.Header
          className={cx(Styles.modalHeader, { [Styles.withBackButton]: !!onBack })}
          preventClose
          compact={false}
        >
          <div className={Styles.header}>
            {onBack && <Modal.BackButton onPress={onBack} />}
            <Heading level={3} id="create-new-header">
              Create New
            </Heading>
            <div
              {...hoverProps}
              className={Styles.searchWrapperOuter}
              onClick={() => {
                searchRef.current?.focus()
              }}
            >
              {subCategory && (
                <IconButton
                  className={Styles.backButton}
                  data-testid="wizards-v3-back-button"
                  aria-label="Back"
                  variant="tertiary"
                  onPress={() => handleBack()}
                >
                  <ChevronLeftSmall />
                </IconButton>
              )}
              <div className={Styles.searchWrapper}>
                <SearchInput
                  name="Search"
                  placeholder="Search for wizards, instagram, blog, plan, turn into, campaign..."
                  autoFocus
                  icon={Search}
                  value={search}
                  noDebounce
                  ref={searchRef}
                  onChange={(val) => setSearch(val)}
                  onClear={() => setSearch('')}
                  isParentHovered={isHovered}
                  clearable
                  autoComplete="off"
                  className={Styles.searchInput}
                  wrapperClassName={Styles.searchInputWrapper}
                  size={isSystemRedesignEnabled ? 'lg' : 'md'}
                />
              </div>
            </div>
          </div>
        </Modal.Header>
        <div className={Styles.content}>
          {!recentWizardsQuery.isLoading && (
            <>
              <Sidebar
                showNewProjectButton={showNewProjectButton}
                parentFolder={parentFolder}
                selectedCategory={selectedCategory}
                setSelectedCategory={_setSelectedCategory}
                search={search}
                usingRecentWizards={!!recentWizards}
                forYouPageType={forYouPageType}
                subCategory={subCategory}
              />
              <div
                className={Styles.sectionContent}
                style={{
                  opacity: isVisible ? 1 : 0,
                }}
              >
                {renderSection()}
              </div>
            </>
          )}
        </div>
      </Modal.Root>
    )
  },
)
